const revealSelector = [
  "[data-motion-reveal]",
  ".motion-reveal",
  ".editorial-image",
  ".content-card",
  ".package-card",
  ".service-card",
  ".industry-pattern-grid",
  ".industry-sample-card",
  ".final-cta .section-inner"
].join(", ");
const rippleSelector = ".button, .text-link, .tool-index-card, [data-motion-ripple]";
const HERO_CURRENT_SCROLL_RATIO = 0.12;
const COUNT_DURATION_MS = 650;
const preparedCountTargets = new WeakSet();
const startedCountTargets = new WeakSet();
const completedCountTargets = new WeakSet();

export function isReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function resolveReveal(target) {
  target.classList.remove("is-observed");
  target.classList.add("is-visible");
}

export function observeReveals() {
  const targets = Array.from(document.querySelectorAll(revealSelector));

  if (isReducedMotion()) {
    targets.forEach(resolveReveal);
    return null;
  }

  if (!("IntersectionObserver" in window)) {
    return null;
  }

  const staggerGroups = new Map();
  let observer;
  try {
    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const groupedTargets = staggerGroups.get(entry.target);
        if (groupedTargets) {
          groupedTargets.forEach((target) => target.classList.add("is-visible"));
        } else {
          entry.target.classList.add("is-visible");
        }
        observer.unobserve(entry.target);
      });
    }, {
      rootMargin: "0px 0px -8% 0px",
      threshold: 0.08
    });
  } catch {
    return null;
  }

  targets.forEach((target) => {
    target.classList.add("motion-reveal", "is-observed");
    const stagger = target.closest("[data-motion-stagger], .motion-stagger");
    if (stagger) {
      stagger.classList.add("motion-stagger");
      const siblings = Array.from(stagger.querySelectorAll(revealSelector));
      const index = Math.max(0, siblings.indexOf(target));
      target.style.setProperty("--motion-stagger-index", String(Math.min(index, 6)));
      const groupedTargets = staggerGroups.get(stagger) ?? [];
      groupedTargets.push(target);
      staggerGroups.set(stagger, groupedTargets);
    } else {
      try {
        observer.observe(target);
      } catch {
        resolveReveal(target);
      }
    }
  });

  staggerGroups.forEach((groupedTargets, stagger) => {
    try {
      observer.observe(stagger);
    } catch {
      groupedTargets.forEach(resolveReveal);
    }
  });

  return observer;
}

export function bindHeroCurrent() {
  const field = document.querySelector("[data-current-field]");
  if (!field) return null;

  if (isReducedMotion()) {
    field.classList.remove("is-current-active");
    field.style.setProperty("--hero-current-offset", "0px");
    return null;
  }

  if (!("IntersectionObserver" in window)) {
    return null;
  }

  const hero = field.closest("[data-motion-hero]");
  if (!hero) return null;

  let fieldVisible = false;
  let framePending = false;

  function updateFieldOffset() {
    framePending = false;
    if (!fieldVisible) return;
    const scrollDistance = Math.max(0, window.scrollY);
    const offset = Math.round(scrollDistance * HERO_CURRENT_SCROLL_RATIO * 1000) / 1000;
    field.style.setProperty("--hero-current-offset", `${offset === 0 ? 0 : -offset}px`);
  }

  function scheduleFieldUpdate() {
    if (!fieldVisible || framePending) return;
    framePending = true;
    window.requestAnimationFrame(updateFieldOffset);
  }

  let observer;
  try {
    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.target !== hero) return;
        fieldVisible = entry.isIntersecting;
        field.classList.toggle("is-current-active", fieldVisible);
        if (fieldVisible) scheduleFieldUpdate();
      });
    }, {
      rootMargin: "0px",
      threshold: 0
    });
    observer.observe(hero);
  } catch {
    observer?.disconnect();
    return null;
  }

  window.addEventListener("scroll", scheduleFieldUpdate, { passive: true });
  return observer;
}

function createRipple(target, clientX, clientY) {
  if (!target || isReducedMotion()) return;

  if (target.querySelector(":scope > .motion-ripple")) return;
  target.classList.add("has-motion-ripple");

  const bounds = target.getBoundingClientRect();
  const size = Math.max(bounds.width, bounds.height) * 2;
  const ripple = document.createElement("span");
  ripple.className = "motion-ripple";
  ripple.setAttribute("aria-hidden", "true");
  ripple.style.setProperty("--motion-ripple-size", `${size}px`);
  ripple.style.setProperty("--motion-ripple-x", `${clientX - bounds.left}px`);
  ripple.style.setProperty("--motion-ripple-y", `${clientY - bounds.top}px`);

  const removeRipple = () => {
    ripple.remove();
    if (!target.querySelector(":scope > .motion-ripple")) {
      target.classList.remove("has-motion-ripple");
    }
  };
  ripple.addEventListener("animationend", removeRipple, { once: true });
  window.setTimeout(removeRipple, 700);
  target.append(ripple);
}

export function bindPointerRipples() {
  if (isReducedMotion()) return;

  const finePointer = window.matchMedia("(hover: hover) and (pointer: fine)");
  document.addEventListener("pointerdown", (event) => {
    if (!finePointer.matches || !event.isPrimary || event.button !== 0) return;
    const target = event.target.closest(rippleSelector);
    if (!target) return;
    createRipple(target, event.clientX, event.clientY);
  }, { passive: true });
}

export function bindThemeRipple() {
  if (isReducedMotion()) return;

  document.addEventListener("upstream:modechange", (event) => {
    const selected = event.detail?.control ?? document.querySelector(
      '[data-color-mode-option][aria-pressed="true"]'
    );
    if (!selected) return;
    const bounds = selected.getBoundingClientRect();
    createRipple(
      selected,
      bounds.left + (bounds.width / 2),
      bounds.top + (bounds.height / 2)
    );
  });
}

function countText(target, value) {
  const finalValue = Number(target.dataset.motionCount);
  const decimalPlaces = (String(target.dataset.motionCount).split(".")[1] ?? "").length;
  const displayValue = decimalPlaces > 0
    ? Math.min(value, finalValue).toFixed(decimalPlaces)
    : String(Math.round(Math.min(value, finalValue)));
  target.textContent = `${target.dataset.motionPrefix ?? ""}${displayValue}${target.dataset.motionSuffix ?? ""}`;
}

function finishCount(target) {
  countText(target, Number(target.dataset.motionCount));
  completedCountTargets.add(target);
}

function animateCount(target) {
  const finalValue = Number(target.dataset.motionCount);
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  let startedAt = null;
  let frameId = null;
  let finished = false;

  function complete() {
    if (finished) return;
    finished = true;
    if (frameId !== null) {
      window.cancelAnimationFrame?.(frameId);
      frameId = null;
    }
    reducedMotion.removeEventListener?.("change", handleMotionChange);
    finishCount(target);
  }

  function handleMotionChange(event) {
    if (event.matches) complete();
  }

  function scheduleFrame() {
    const scheduledFrame = window.requestAnimationFrame(update);
    if (!finished) frameId = scheduledFrame;
  }

  function update(timestamp) {
    frameId = null;
    if (reducedMotion.matches) {
      complete();
      return;
    }
    if (startedAt === null) startedAt = timestamp;
    const progress = Math.min(1, (timestamp - startedAt) / COUNT_DURATION_MS);
    const easedProgress = 1 - ((1 - progress) ** 3);
    countText(target, finalValue * easedProgress);
    if (progress < 1) {
      scheduleFrame();
    } else {
      complete();
    }
  }

  if (reducedMotion.matches) {
    finishCount(target);
    return;
  }
  reducedMotion.addEventListener?.("change", handleMotionChange);
  scheduleFrame();
}

function observeCountTargets(root) {
  const targets = Array.from(root.querySelectorAll("[data-motion-count]"))
    .filter((target) => !preparedCountTargets.has(target));
  if (targets.length === 0) return null;

  if (isReducedMotion() || !("IntersectionObserver" in window)) {
    targets.forEach((target) => {
      preparedCountTargets.add(target);
      finishCount(target);
    });
    return null;
  }

  let observer;
  try {
    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || startedCountTargets.has(entry.target)) return;
        startedCountTargets.add(entry.target);
        observer.unobserve(entry.target);
        if (isReducedMotion()) {
          finishCount(entry.target);
          return;
        }
        animateCount(entry.target);
      });
    }, {
      rootMargin: "0px",
      threshold: 0.35
    });
  } catch {
    targets.forEach((target) => {
      preparedCountTargets.add(target);
      finishCount(target);
    });
    return null;
  }

  targets.forEach((target) => {
    const value = Number(target.dataset.motionCount);
    preparedCountTargets.add(target);

    /* PORT CHANGE: a counter inside a live region must not animate.
       The tools inject their results into containers marked
       aria-live="polite" aria-atomic="true", and an animated count mutates
       that container on every frame, so a screen reader re-announces the whole
       result panel hundreds of times for one calculation. Rendering the final
       value immediately gives exactly one announcement, which is what a
       listener actually wants from a result. */
    if (target.closest("[aria-live]")) {
      finishCount(target);
      return;
    }
    if (!Number.isFinite(value) || value < 0) {
      completedCountTargets.add(target);
      return;
    }
    countText(target, 0);
    try {
      observer.observe(target);
    } catch {
      finishCount(target);
    }
  });
  return observer;
}

export function bindCountUps() {
  const observer = observeCountTargets(document);
  document.addEventListener("upstream:numericresults", (event) => {
    const root = event.detail?.root;
    if (root?.querySelectorAll) observeCountTargets(root);
  });
  return observer;
}

export function bindMobileHeader() {
  const header = document.querySelector("[data-site-header]");
  if (!header) return;

  const reducedMotion = isReducedMotion();
  const mobileViewport = window.matchMedia("(max-width: 47rem)");
  let lastScrollY = window.scrollY;
  let framePending = false;

  function updateHeader() {
    framePending = false;
    const currentScrollY = Math.max(0, window.scrollY);
    const delta = currentScrollY - lastScrollY;
    const menuOpen = document.body.classList.contains("menu-open");

    header.classList.toggle("is-scrolled", currentScrollY > 24);
    if (reducedMotion || !mobileViewport.matches || menuOpen || currentScrollY < 96) {
      header.classList.remove("is-header-hidden");
    } else if (delta > 8) {
      header.classList.add("is-header-hidden");
    } else if (delta < -8) {
      header.classList.remove("is-header-hidden");
    }

    lastScrollY = currentScrollY;
  }

  function scheduleHeaderUpdate() {
    if (framePending) return;
    framePending = true;
    window.requestAnimationFrame(updateHeader);
  }

  updateHeader();
  window.addEventListener("scroll", scheduleHeaderUpdate, { passive: true });
  window.addEventListener("resize", scheduleHeaderUpdate, { passive: true });
  mobileViewport.addEventListener?.("change", scheduleHeaderUpdate);
}

function initializeMotion() {
  /* PORT CHANGE: reveals are owned by assets/upstream-motion-plus.js, which
     uses a two-observer system (large sections trigger earlier than small
     cards) and a longer exponential ease. Calling observeReveals() here as
     well would put two IntersectionObservers on every target and make the
     reveal timing inconsistent. The rest of this engine, the River Cut field,
     ripples, count-ups and the header, is unchanged. */
  bindHeroCurrent();
  bindPointerRipples();
  bindThemeRipple();
  bindCountUps();
  bindMobileHeader();
  document.documentElement.classList.add("motion-ready");
}

if (typeof document !== "undefined") {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initializeMotion, { once: true });
  } else {
    initializeMotion();
  }
}


/* ---------------------------------------------------------------------------
   Shopify theme editor lifecycle.

   Everything above is the static build's motion module, ported unchanged.
   It binds once on DOMContentLoaded, which is correct for a static page but
   wrong inside the Shopify theme editor: when a merchant edits, adds, or
   reorders a section, Shopify replaces that section's markup wholesale. The
   new nodes are unbound, so without this shim an edited section renders with
   its reveals stuck invisible and its counters stuck at zero.

   Deliberately NOT re-calling observeReveals() or initializeMotion() here.
   Those are not idempotent: each call builds a fresh IntersectionObserver over
   every reveal target and re-adds window scroll listeners, so repeated editor
   reloads would stack duplicate observers and listeners.

   Instead: reveal the new section's targets directly (the merchant needs the
   content visible and correct, not a replayed entrance), and reuse the
   module's own scoped, WeakSet-guarded count-up entry point.
--------------------------------------------------------------------------- */
(function bindThemeEditorLifecycle() {
  if (typeof document === "undefined") return;

  var editorRevealSelector = [
    "[data-motion-reveal]",
    ".motion-reveal",
    ".editorial-image",
    ".content-card",
    ".package-card",
    ".service-card",
    ".industry-pattern-grid",
    ".industry-sample-card",
    ".final-cta .section-inner"
  ].join(", ");

  function revealWithin(root) {
    if (!root || !root.querySelectorAll) return;
    root.querySelectorAll(editorRevealSelector).forEach(function (target) {
      target.classList.add("motion-reveal");
      target.classList.remove("is-observed");
      target.classList.add("is-visible");
    });
  }

  function refreshCounts(root) {
    if (!root) return;
    document.dispatchEvent(new CustomEvent("upstream:numericresults", {
      detail: { root: root }
    }));
  }

  function handleSectionLoad(event) {
    var root = event.target || document;
    document.documentElement.classList.add("motion-ready");
    revealWithin(root);
    refreshCounts(root);
  }

  document.addEventListener("shopify:section:load", handleSectionLoad);
  document.addEventListener("shopify:section:reorder", handleSectionLoad);
  document.addEventListener("shopify:block:select", handleSectionLoad);
}());
